import { useState, useCallback, useEffect } from 'react'
import classnames from 'classnames'
import Formsy from 'formsy-react'

import { T, withTranslate } from '@i18n/utils'

import useRequestPending from 'shared/hooks/useRequestPending'
import useCheckoutValidation from 'shared/hooks/useCheckoutValidation'
import {
    CHECKOUT_FIELD_PAYMENT_METHOD,
    CHECKOUT_FIELD_TERMS,
    CHECKOUT_FIELD_BILLING_ADDRESS,
} from 'shared/checkout-validation'
import { isAddressFilled } from 'shared/utils'
import { useSendGTAGEvent } from 'analytics/utils'

import { CardSection } from '@components/CardSection'
import ErrorMessage from '@components/ErrorMessage'
import Button from '@components/Button'
import PaymentMethodSection from '@components/PaymentMethodSection'

import FooterSection from '../FooterSection'
import AddressSection from '../AddressSection'
import BoletoInfo from './BoletoInfo'
import ProductDetails from './ProductDetails'
import PromoCodeSection from './PromoCodeSection'

import { billingAddressSelector, deliveryAddressSelector, userSelector } from 'auth/selectors'
import { authVisible } from 'header/actions'
import { clearPending } from 'api/actions'

import { promoCodeSelector, makePurchasePendingSelector } from 'booking/selectors'
import { selectCreditCard, selectVehicle, makePurchase } from 'booking/actions'
import { selectedCreditCardSelector, selectedSeasonTicketSelector, selectedVehicleSelector } from 'booking/selectors'
import { getPaymentMethod } from 'shared/transactions'
import { RateCurrency } from 'shared/price-utilities'
import featureToggles from 'shared/featureToggles'
import { ANALYTICS_FLOW_TYPE_SUBSCRIPTION } from 'analytics/constants'

import styles from '../styles.module.scss'

const ReviewPay = ({
    purchaseData,
    billingAddress,
    deliveryAddress,
    user,
    makePurchase,
    clearPending,
    selectedCreditCard,
    selectCreditCard,
    selectedVehicle,
    selectedSeasonTicket = {},
    makePurchasePending,
    hideCreditCardPayment,
    promoCode,
}) => {
    const sendGTAGEvent = useSendGTAGEvent()
    const { product, startDate, vehicles, details } = purchaseData
    const { rate } = selectedSeasonTicket

    const { error, isLoading } = useRequestPending(makePurchasePending)
    const isBillingAddressFilled = isAddressFilled(billingAddress)

    const discountedTotalPrice = promoCode && promoCode.statusCode !== -1 && promoCode.discountTotal

    const promoCodeBtnDisabled = !rate || makePurchasePending === true

    const [termsAccepted, setTermsAccepted] = useState(false)

    /** BOLETO */
    const [isBoletoVisible, setIsBoletoVisible] = useState(false)
    const [formBoletoData, setFormBoletoData] = useState(null)
    const [isFormBoletoValid, setIsFormBoletoValid] = useState(false)
    const [isUsingBoleto, setIsUsingBoleto] = useState(false)

    const setCreditCard = useCallback(
        card => {
            selectCreditCard(card)
            setIsUsingBoleto(false)
        },
        [selectCreditCard]
    )

    const setUsingBoleto = useCallback(() => {
        setIsUsingBoleto(true)
        selectCreditCard(null)
    }, [selectCreditCard])

    const boletoClassName = classnames(styles.boleto, {
        [styles.selected]: isUsingBoleto,
    })

    const isAddBoletoButtonVisible = user?.details.document.type === 'CNPJ'

    const isPaymentSet = isUsingBoleto ? isFormBoletoValid : !!selectedCreditCard

    // The Buy button stays enabled; clicking it points out everything that is still missing
    const { errors, validate } = useCheckoutValidation({
        [CHECKOUT_FIELD_PAYMENT_METHOD]: !isPaymentSet && <T>checkout.validation.payment.method.missing</T>,
        [CHECKOUT_FIELD_TERMS]: !termsAccepted && <T>checkout.validation.terms.missing</T>,
        // AddressSection already shows its own message, this only blocks the purchase
        [CHECKOUT_FIELD_BILLING_ADDRESS]: !isBillingAddressFilled,
    })

    const onPayForParkingSubmit = () => {
        if (!validate()) return

        const boletoData = isUsingBoleto && isFormBoletoValid ? formBoletoData : null

        if (selectedCreditCard) {
            sendGTAGEvent('payment_method_selected', {
                payment_type: getPaymentMethod(selectedCreditCard.id),
            })
        }

        makePurchase({
            rate: {
                ...rate,
                eDataLocationId: rate.eDataLocationId || rate.locationId,
            },
            vehicles: selectedVehicle,
            boletoData,
            promoCode,
            ...details,
        })
    }

    useEffect(() => {
        clearPending(['makePurchase'])
    }, [])

    if (!rate) return null

    return (
        <CardSection>
            <>
                <ProductDetails product={product} rate={rate} startDate={startDate} vehicles={vehicles} />
                <hr className="my-4" />
                <div className={classnames('col-12', 'col-xs-9', 'text-sm-right', styles.reviewTotal)}>
                    <div className={classnames('d-flex justify-content-end', styles.total)}>
                        <div className="mr-3">
                            <T>subscription.purchase.invoice.total</T>{' '}
                        </div>
                        <div>
                            {discountedTotalPrice != null && (
                                <span className={classnames('font-weight-bold', styles.originalAmount)}>
                                    <RateCurrency value={rate.recurringAmount} currency={rate.currencySymbol} />
                                </span>
                            )}
                            <span className="font-weight-bold">
                                <RateCurrency
                                    rate={rate}
                                    value={discountedTotalPrice != null ? discountedTotalPrice : rate?.recurringAmount}
                                />
                            </span>
                        </div>
                    </div>
                </div>
                {featureToggles.isColumbia && (
                    <PromoCodeSection rate={rate} promoCodeBtnDisabled={promoCodeBtnDisabled} />
                )}
                <AddressSection billingAddress={billingAddress} deliveryAddress={deliveryAddress} />
                <PaymentMethodSection
                    selectedPaymentMethod={selectedCreditCard}
                    setPaymentMethod={setCreditCard}
                    paymentMethods={user?.creditCards}
                    hideCreditCardPayment={hideCreditCardPayment}
                    analyticsFlowType={ANALYTICS_FLOW_TYPE_SUBSCRIPTION}
                    error={errors[CHECKOUT_FIELD_PAYMENT_METHOD]}
                />
                {isAddBoletoButtonVisible && !isBoletoVisible && (
                    <Button
                        size="small"
                        btnStyle="link"
                        iconLeft="plus-round"
                        onClick={() => {
                            setIsBoletoVisible(true)
                            setUsingBoleto()
                        }}>
                        <T>subscription.purchase.addboleto</T>
                    </Button>
                )}
                {isBoletoVisible && (
                    <BoletoInfo
                        boletoClassName={boletoClassName}
                        setUsingBoleto={setUsingBoleto}
                        setFormBoletoData={setFormBoletoData}
                        setIsFormBoletoValid={setIsFormBoletoValid}
                    />
                )}
                <Formsy noValidate onSubmit={onPayForParkingSubmit} onInvalidSubmit={validate}>
                    <FooterSection
                        disabled={isLoading}
                        loading={isLoading}
                        currencySymbol={rate.currencySymbol}
                        totalValue={discountedTotalPrice || rate.amount}
                        termsAccepted={termsAccepted}
                        setTermsAccepted={setTermsAccepted}
                        termsError={errors[CHECKOUT_FIELD_TERMS]}
                    />
                </Formsy>
            </>
            {error && <ErrorMessage className="mt-5" messages={[error]} />}
        </CardSection>
    )
}

export default compose(
    withTranslate,
    connect(
        createStructuredSelector({
            billingAddress: billingAddressSelector,
            deliveryAddress: deliveryAddressSelector,
            user: userSelector,
            selectedCreditCard: selectedCreditCardSelector,
            selectedVehicle: selectedVehicleSelector,
            selectedSeasonTicket: selectedSeasonTicketSelector,
            makePurchasePending: makePurchasePendingSelector,
            promoCode: promoCodeSelector,
        }),
        {
            authVisible,
            makePurchase,
            selectCreditCard,
            selectVehicle,
            clearPending,
        }
    )
)(ReviewPay)
